import { makeAutoObservable, observable } from 'mobx';

import type { MembershipManageGateway } from '../api/MembershipManageGateway';
import {
  appliedPlanChange,
  asManageError,
  benefitsEnd,
  canChangePlan,
  type MembershipManageEvent,
  type MembershipManageFailure,
  type MembershipManageIncident,
  type MembershipManageOperation,
} from '../domain/manage';
import {
  type MembershipNotice,
  type MembershipPlanRef,
  toPlanRef,
} from '../domain/purchase';
import type { Membership } from '../domain/types';
import { isManageSettled, manageErrorOutcome } from './manageOutcome';

/**
 * How a request of the member card ended: the sheet closes on `done` and
 * stays open on `failed`; `upgraded` is a plan switch the backend applied at
 * once, which the sheet ends on "{plan} added to your plan".
 */
export type MembershipManageResult = 'done' | 'upgraded' | 'failed' | 'ignored';

/** Everything the store needs from the outside world. */
export interface MembershipManageStoreDeps {
  gateway: MembershipManageGateway;
  /** Shows a membership from an answer in place, without a skeleton, with an optional banner. */
  applyMembership: (
    membership: Membership | null,
    notice?: MembershipNotice,
  ) => void;
  showNotice: (notice: MembershipNotice) => void;
  /** Re-reads `/me` in place after a conflict. */
  reloadMembership: () => void;
  /** Re-reads the zone's plans. */
  reloadPlans: () => void;
  track: (event: MembershipManageEvent) => void;
  report: (incident: MembershipManageIncident) => void;
  /** Another operation of the membership runs (the wait for restored benefits); no request may start. */
  isBlocked: () => boolean;
}

/**
 * Cancel, plan switch and "Keep current plan" of the member card. One
 * operation at a time: a second click while a request is in flight, or any
 * request while the restoration wait runs, is ignored. Every request
 * is sent once; an unknown outcome is checked through `/me` and never re-sent.
 * The answer is the membership itself, shown in place with a result banner.
 */
export class MembershipManageStore {
  /** The request in flight, or being checked through `/me`. */
  operation: MembershipManageOperation | null = null;
  failure: MembershipManageFailure | null = null;

  private epoch = 0;

  constructor(private readonly deps: MembershipManageStoreDeps) {
    makeAutoObservable<MembershipManageStore, 'deps' | 'epoch'>(
      this,
      { failure: observable.ref, deps: false, epoch: false },
      { autoBind: true },
    );
  }

  /** A request of the member card is in flight or being checked. */
  get isBusy(): boolean {
    return this.operation !== null;
  }

  /** No request may start: one is in flight, or another operation of the membership runs. */
  get isLocked(): boolean {
    return this.isBusy || this.deps.isBlocked();
  }

  /** The failure of `operation`, for the place that asked for it. */
  failureOf(
    operation: MembershipManageOperation,
  ): MembershipManageFailure | null {
    return this.failure?.operation === operation ? this.failure : null;
  }

  /** A sheet opened or closed: its old failure is no longer relevant. */
  clearFailure(): void {
    this.failure = null;
  }

  cancel(membership: Membership): Promise<MembershipManageResult> {
    return this.run('cancel', membership, null, () =>
      this.deps.gateway.cancel(),
    );
  }

  /**
   * Switches to `plan`. Ignored without a request when the membership does
   * not allow a switch (cancelled, overdue, granted, a switch scheduled): a
   * stale link must not reach the backend.
   */
  changePlan(
    membership: Membership,
    plan: MembershipPlanRef,
  ): Promise<MembershipManageResult> {
    if (!canChangePlan(membership)) {
      return Promise.resolve('ignored');
    }
    const target = toPlanRef(plan);
    return this.run('changePlan', membership, target, () =>
      this.deps.gateway.changePlan(target.id),
    );
  }

  keepPlan(membership: Membership): Promise<MembershipManageResult> {
    return this.run('keepPlan', membership, null, () =>
      this.deps.gateway.cancelPlanChange(),
    );
  }

  /** Logout: drops answers in flight and forgets the failure. */
  reset(): void {
    this.epoch += 1;
    this.operation = null;
    this.failure = null;
  }

  private async run(
    operation: MembershipManageOperation,
    before: Membership,
    target: MembershipPlanRef | null,
    send: () => Promise<Membership>,
  ): Promise<MembershipManageResult> {
    if (this.isLocked) {
      return 'ignored';
    }
    const epoch = this.epoch;
    this.operation = operation;
    this.failure = null;
    try {
      const after = await send();
      if (epoch !== this.epoch) {
        return 'ignored';
      }
      return this.succeed(operation, before, target, after);
    } catch (error) {
      if (epoch !== this.epoch) {
        return 'ignored';
      }
      return this.handleError(operation, before, target, error, epoch);
    }
  }

  private async handleError(
    operation: MembershipManageOperation,
    before: Membership,
    target: MembershipPlanRef | null,
    error: unknown,
    epoch: number,
  ): Promise<MembershipManageResult> {
    const outcome = manageErrorOutcome(operation, asManageError(error));
    if (outcome.incident) {
      this.deps.report(outcome.incident);
    }
    if (outcome.next === 'reload') {
      this.reload(outcome.reloadPlans, outcome.notice);
      return 'done';
    }
    if (outcome.next === 'fail') {
      this.fail(operation, outcome.reason);
      return 'failed';
    }
    const after = await this.deps.gateway.getMe().then(
      (membership) => membership,
      () => undefined,
    );
    if (epoch !== this.epoch) {
      return 'ignored';
    }
    if (
      after !== undefined &&
      isManageSettled(operation, after, target?.id ?? null)
    ) {
      return this.succeed(operation, before, target, after);
    }
    if (operation === 'cancel' && after === null) {
      this.cancelled(before, null);
      return 'done';
    }
    this.settleUnknown(operation, after, outcome.unsettledReason);
    return 'failed';
  }

  /**
   * Shows the answer of a request that went through. A switch answered with
   * a membership that shows neither the new plan nor a scheduled switch broke
   * the contract: it is shown as it is, reported, and fails.
   */
  private succeed(
    operation: MembershipManageOperation,
    before: Membership,
    target: MembershipPlanRef | null,
    after: Membership,
  ): MembershipManageResult {
    switch (operation) {
      case 'changePlan':
        return this.planChanged(before, target, after);
      case 'cancel':
        this.cancelled(before, after);
        return 'done';
      case 'keepPlan':
        this.planKept(before, after);
        return 'done';
    }
  }

  /** The request is over and went through: the card is free. */
  private settle(): void {
    this.operation = null;
    this.failure = null;
  }

  /** A cancel that went through; `after` is `null` when `/me` no longer finds the membership. */
  private cancelled(before: Membership, after: Membership | null): void {
    this.settle();
    this.deps.applyMembership(after, {
      kind: 'cancelled',
      benefitsUntil: after?.benefitsActive ? benefitsEnd(after) : null,
    });
    this.deps.track({
      name: 'cancelled',
      payload: { planCode: before.plan.code, status: before.status },
    });
  }

  private planKept(before: Membership, after: Membership): void {
    this.settle();
    this.deps.applyMembership(after, {
      kind: 'planKept',
      planName: after.plan.name,
    });
    this.deps.track({
      name: 'planChangeKept',
      payload: {
        planCode: before.plan.code,
        scheduledPlanCode: before.scheduledChange?.planCode ?? null,
      },
    });
  }

  private planChanged(
    before: Membership,
    target: MembershipPlanRef | null,
    after: Membership,
  ): MembershipManageResult {
    const direction = target ? appliedPlanChange(after, target.id) : null;
    if (!direction || !target) {
      this.deps.applyMembership(after);
      this.deps.report({
        reason: 'manageFailed',
        operation: 'changePlan',
        kind: 'unknown_outcome',
      });
      this.fail('changePlan', 'failed');
      return 'failed';
    }
    this.settle();
    this.deps.applyMembership(
      after,
      direction === 'upgrade'
        ? { kind: 'upgraded', planName: after.plan.name }
        : undefined,
    );
    this.deps.track({
      name: 'planChanged',
      payload: {
        planCode: before.plan.code,
        toPlanCode: target.code,
        direction,
      },
    });
    return direction === 'upgrade' ? 'upgraded' : 'done';
  }

  private reload(reloadPlans: boolean, notice: MembershipNotice | null): void {
    this.operation = null;
    this.deps.reloadMembership();
    if (reloadPlans) {
      this.deps.reloadPlans();
    }
    if (notice) {
      this.deps.showNotice(notice);
    }
  }

  /**
   * `/me` did not confirm the request: shows what it said and `reason`;
   * `undefined` means `/me` failed too and `null` that the membership is
   * gone, both a plain failure.
   */
  private settleUnknown(
    operation: MembershipManageOperation,
    after: Membership | null | undefined,
    reason: MembershipManageFailure['reason'],
  ): void {
    if (after === undefined) {
      this.fail(operation, 'failed');
      return;
    }
    this.deps.applyMembership(after);
    this.fail(operation, after === null ? 'failed' : reason);
  }

  private fail(
    operation: MembershipManageOperation,
    reason: MembershipManageFailure['reason'],
  ): void {
    this.operation = null;
    this.failure = { operation, reason };
  }
}
